/**
 * Path containment helper for the sidebar file-manager.
 *
 * Every mutating FS op (rename, create, duplicate, move, paste) is checked
 * against the active workspace root *before* it reaches `platform.fs`, so a
 * malformed name like `../../etc/passwd` is rejected at the UI layer rather
 * than relying on the OS / Tauri scope. The Rust commands accept any
 * absolute path that passes their basic validation — containment is the
 * UI's job.
 *
 * The check is purely textual. It normalises separators on both sides and
 * compares prefixes, so it works for both POSIX and Windows paths. It does
 * *not* resolve symlinks or `..` segments — callers must pass already-joined
 * absolute paths. (When the user types a name into the inline-rename input,
 * the calling code is the one joining it to the parent dir; that join is
 * what gets passed here.)
 */

/**
 * Normalise a path for prefix comparison:
 *   - convert Windows `\\` to `/`
 *   - drop a trailing slash (so `/a/` and `/a` compare equal)
 */
function norm(p: string): string {
  const slashed = p.replace(/\\/g, '/');
  return slashed.length > 1 && slashed.endsWith('/') ? slashed.slice(0, -1) : slashed;
}

/**
 * True when `path` equals `root` or lives strictly inside it. False for
 * sibling paths that share a prefix (e.g. `/a/foo` vs root `/a/foobar`),
 * thanks to the trailing-separator check.
 */
export function isUnderRoot(path: string, root: string): boolean {
  if (!path || !root) return false;
  const p = norm(path);
  const r = norm(root);
  if (p === r) return true;
  return p.startsWith(`${r}/`);
}

/** Path separator the platform uses (Windows = `\\`, otherwise `/`). */
function pickSeparator(sample: string): string {
  return sample.includes('\\') && !sample.includes('/') ? '\\' : '/';
}

/**
 * Join a parent directory with a child name. Picks the separator from the
 * parent so a Windows-style parent stays Windows-style. The child must be a
 * single segment — callers reject names containing `/` or `\\` upstream
 * (see {@link isValidName}).
 */
export function joinPath(parent: string, name: string): string {
  const sep = pickSeparator(parent);
  const p = norm(parent).replace(/\//g, sep);
  return p.endsWith(sep) ? `${p}${name}` : `${p}${sep}${name}`;
}

/** Parent directory of `path`, or empty string if there is none. */
export function dirOf(path: string): string {
  const p = norm(path);
  const idx = p.lastIndexOf('/');
  if (idx <= 0) return '';
  return p.slice(0, idx);
}

/** Basename of `path`. */
export function basename(path: string): string {
  const p = norm(path);
  const idx = p.lastIndexOf('/');
  return idx < 0 ? p : p.slice(idx + 1);
}

/**
 * Human-readable label for a workspace root. Like {@link basename} (so a single
 * trailing slash is ignored) but never yields a blank/dotty label:
 *   - `''`, `'.'`, `'..'` (or whitespace-only) fall back to the full `path`,
 *   - the filesystem root `'/'` stays `'/'`,
 *   - an empty `path` returns `''` (caller decides what to do — no crash).
 *
 * Defends against an empty sidebar folder name when a path arrives un-/under-
 * canonicalized (e.g. `slate .` → a path ending in a slash).
 */
export function workspaceLabel(path: string): string {
  if (!path) return '';
  const base = basename(path).trim();
  if (base === '' || base === '.' || base === '..') {
    // No usable last segment. Root stays '/', otherwise show the whole path.
    return norm(path) === '/' ? '/' : path;
  }
  return base;
}

/**
 * True when `name` is a syntactically valid single-segment filename. Rejects
 * empty, `.`, `..`, anything with a path separator, and the NUL byte. We
 * keep this deliberately permissive — OS-level checks (illegal chars on
 * Windows, length limits) surface as FsError::Io at the bridge.
 */
export function isValidName(name: string): boolean {
  if (!name) return false;
  const trimmed = name.trim();
  if (!trimmed) return false;
  if (trimmed === '.' || trimmed === '..') return false;
  if (trimmed.includes('/') || trimmed.includes('\\')) return false;
  if (trimmed.includes('\0')) return false;
  return true;
}
